import { type AuthProviderView, isPlatformError, LoginBody } from '@agentic/contracts';
import { Banner, Button, buttonClass, Field, Input } from '@agentic/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AccountSetupBanner } from '../app/account-setup.tsx';
import { client } from '../app/api.ts';
import { config } from '../app/config.ts';
import { keys } from '../app/queries.ts';
import { type ErrorParts, errorParts, ReferenceLine, TechnicalDetails } from '../app/route-states.tsx';
import { ProductMark } from '../app/shell.tsx';

const Search = z.object({ redirect: z.string().optional().catch(undefined) });

export const Route = createFileRoute('/login')({
  validateSearch: Search,
  component: LoginPage,
});

const DEMO_USERS = [
  ['requester', 'creates procurement tasks'],
  ['approver', 'decides approvals'],
  ['operator', 'registry, modules, admin, chaos'],
  ['analyst', 'finance, narrow ACL'],
  ['csr', 'customer-service cases'],
] as const;

/**
 * Sign-in options: `GET /auth/providers` (idp-chain) lists company SSO providers and the break-glass local
 * administrator. Under a single local provider the route does not exist and the page shows the password form only.
 */
function useProviders(): AuthProviderView[] | undefined {
  const q = useQuery({
    queryKey: ['auth', 'providers'],
    queryFn: async () => {
      try {
        return (await client.auth.providers()).providers;
      } catch (e) {
        if (isPlatformError(e) && e.code === 'not_found') return [];
        throw e;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
  return q.data;
}

/** Full-page navigation to an SSO provider's login route through the API base (`/api/auth/oidc/login`). */
export function ssoHref(provider: AuthProviderView, apiBase: string, returnTo: string): string {
  return `${apiBase}${provider.loginUrl}?returnTo=${encodeURIComponent(returnTo)}`;
}

function LoginPage() {
  const { redirect } = Route.useSearch();
  const providers = useProviders();
  const sso = providers?.filter((p) => p.kind === 'oidc') ?? [];
  const breakGlass = providers?.find((p) => p.breakGlass);
  // A provider that accepts its username and password through POST /auth/login (scripted demos) keeps the
  // password form visible; otherwise the form is the collapsed break-glass login.
  const passwordLogin = sso.some((p) => p.passwordLogin);
  const breakGlassForm = !!breakGlass && sso.length > 0 && !passwordLogin;
  const target = redirect?.startsWith('/') && !redirect.startsWith('/login') ? redirect : '/cases';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<(ErrorParts & { loginUrl?: string }) | null>(null);
  const form = useForm<z.infer<typeof LoginBody>>({
    resolver: zodResolver(LoginBody),
    defaultValues: { username: '', password: '' },
  });
  const { errors, isSubmitting } = form.formState;

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const res = breakGlassForm
        ? await client.auth.breakGlassLogin({ body: values })
        : await client.auth.login({ body: values });
      qc.setQueryData(keys.me, { principal: res.principal });
      await navigate({ to: target });
    } catch (e) {
      // 24: one shape for every error surface (code, message and reference), from the shared helper.
      const loginUrl = isPlatformError(e) ? e.details?.loginUrl : undefined;
      setError({
        ...errorParts(e),
        ...(typeof loginUrl === 'string' && loginUrl.startsWith('/') ? { loginUrl } : {}),
      });
    }
  });

  const passwordForm = () => (
    <form onSubmit={onSubmit} noValidate className="mt-6 flex flex-col gap-4">
      <Field id="username" label="Username" error={errors.username?.message}>
        <Input id="username" autoComplete="username" autoFocus {...form.register('username')} />
      </Field>
      <Field id="password" label="Password" error={errors.password?.message}>
        <Input id="password" type="password" autoComplete="current-password" {...form.register('password')} />
      </Field>
      {error?.code === 'account_setup_required' ? (
        <AccountSetupBanner
          href={
            sso[0]
              ? ssoHref(sso[0], config.apiBase, target)
              : error.loginUrl
                ? `${config.apiBase}${error.loginUrl}?returnTo=${encodeURIComponent(target)}`
                : undefined
          }
          code={error.code}
          message={error.message}
        />
      ) : error ? (
        <Banner tone="bad" role="alert" title="Could not sign you in">
          <p>
            {error.code === 'unauthorized'
              ? 'Check the username and password, then try again.'
              : 'The sign-in service did not answer as expected. Try again in a moment.'}
          </p>
          {error.traceId && <ReferenceLine traceId={error.traceId} className="mt-1" />}
          <TechnicalDetails code={error.code} message={error.message} traceId={error.traceId} />
        </Banner>
      ) : null}
      <Button type="submit" variant="primary" loading={isSubmitting} className="mt-2 w-full">
        Sign in
      </Button>
    </form>
  );

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-canvas px-4 py-10">
      <section className="w-full max-w-[400px] wb-card p-8">
        <ProductMark />
        <h1 className="mt-8 text-[24px] leading-tight font-semibold tracking-[-0.02em]">Sign in</h1>
        <p className="mt-1.5 text-ink-2">Case workbench for governed agent work.</p>
        {sso.length > 0 ? (
          <div className="mt-6 flex flex-col gap-3">
            {sso.map((p) => (
              <a
                key={p.loginUrl}
                href={ssoHref(p, config.apiBase, target)}
                className={buttonClass('primary', 'md', 'w-full')}
              >
                Sign in with {p.name === 'Company SSO' ? 'company SSO' : p.name}
              </a>
            ))}
          </div>
        ) : null}
        {breakGlassForm && breakGlass ? (
          <details className="mt-6 rounded-xl border border-line px-4 py-2">
            <summary className="min-h-[44px] cursor-pointer content-center text-[14px] font-medium">
              {breakGlass.name}
            </summary>
            <p className="mt-1 text-[13px] text-ink-2">
              Break-glass access for administrators when company SSO is unavailable. Every use is audited.
            </p>
            {passwordForm()}
          </details>
        ) : (
          <>
            {sso.length > 0 ? (
              <p className="mt-6 text-[13px] text-ink-2">
                Or sign in with your company username and password.
              </p>
            ) : null}
            {passwordForm()}
          </>
        )}
        <p
          className="mt-6 text-[13px] text-ink-2"
          title="Sessions use an HttpOnly cookie; the password is never stored in the browser."
        >
          Trouble signing in? Ask your platform operator to check your username and roles. What you do after
          signing in is recorded in the audit log.
        </p>
      </section>
      <aside className="w-full max-w-[400px] wb-card px-8 py-6" aria-label="Environment">
        {config.apiMode === 'mock' ? (
          <div>
            <div className="text-[13px] font-medium text-ink-3">Mock API accounts</div>
            <p className="mt-1 text-[13px] text-ink-2">
              Password for each is <code className="font-mono text-ink">demo-pass-&lt;username&gt;</code>.
            </p>
            <dl className="mt-3 divide-y divide-line">
              {DEMO_USERS.map(([u, d]) => (
                <div key={u} className="flex items-baseline justify-between gap-4 py-2">
                  <dt className="font-mono text-[13px] text-ink">{u}</dt>
                  <dd className="text-right text-[13px] text-ink-2">{d}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : (
          <div className="text-[13px] text-ink-2">Signed-in users see navigation for their roles only.</div>
        )}
      </aside>
    </main>
  );
}
